import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Heart, LogOut, Download } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

async function downloadReceipt(donationId) {
  const res = await api.get(`/api/donor-portal/donations/${donationId}/receipt.pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url; a.download = `receipt-${donationId.slice(0, 8)}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function money(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function DonorPortal() {
  const { user, signOut } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (user && user.role === "donor") {
        try {
          const r = await api.get("/api/donor-portal/my-donations");
          if (!cancelled) setRows(r.data);
        } catch { /* ignore */ }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "donor") return <Navigate to="/app" replace />;

  const total = rows.reduce((s, d) => s + (d.amount || 0), 0);

  return (
    <div className="min-h-screen bg-background" data-testid="donor-portal">
      <header className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Logo height={26} />
          <div className="flex items-center gap-4">
            <div className="text-sm text-right">
              <div className="font-medium">{user.full_name || user.email}</div>
              <div className="text-xs text-muted-foreground">Donor portal</div>
            </div>
            <button onClick={signOut} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="donor-signout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Welcome back</div>
        <h1 className="serif text-4xl text-primary mt-2">Your giving with us.</h1>
        <p className="text-muted-foreground mt-2">Every gift is tracked, allocated, and reported back to you with full transparency.</p>

        <div className="grid grid-cols-2 gap-4 mt-10">
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Lifetime total</div>
            <div className="serif text-4xl text-primary mt-2">{money(total)}</div>
          </div>
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Gifts recorded</div>
            <div className="serif text-4xl text-primary mt-2">{rows.length}</div>
          </div>
        </div>

        <section className="rounded-xl border border-border bg-card mt-10 overflow-hidden">
          <div className="px-5 py-3 border-b border-border"><h3 className="font-medium text-sm">Your gifts</h3></div>
          {loading ? (
            <div className="py-10 text-center text-muted-foreground text-sm">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Heart size={24} className="mx-auto mb-2 opacity-30" />
              No gifts on file yet. Once an organization records a donation in your name, it appears here.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-muted-foreground bg-accent/30">
                <tr><th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Method</th><th className="text-right px-4 py-2">Amount</th><th className="px-4 py-2"></th></tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id} className="border-t border-border/40" data-testid={`donor-portal-gift-${d.id}`}>
                    <td className="px-4 py-2.5 tabular-nums">{d.received_on}</td>
                    <td className="px-4 py-2.5 text-xs"><span className="px-2 py-0.5 rounded bg-accent">{d.method}</span></td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium">{money(d.amount)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => downloadReceipt(d.id)}
                        data-testid={`donor-receipt-${d.id}`}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border hover:bg-accent">
                        <Download size={12} /> Receipt
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </main>
    </div>
  );
}
